import { useCallback, useEffect, useRef, useState } from "react"

const SAVE_DEBOUNCE_MS = 600

export function useDocsCustomPrompt({
	persisted,
	save,
	debounceMs = SAVE_DEBOUNCE_MS,
}: {
	persisted: string
	save: (value: string) => Promise<boolean>
	debounceMs?: number
}) {
	const [value, setValue] = useState(persisted)
	const timeoutRef = useRef<NodeJS.Timeout>(undefined)
	const isDirtyRef = useRef(false)

	// A sync from the replica while the user types would drop their keystrokes.
	useEffect(() => {
		if (isDirtyRef.current) return
		setValue(persisted)
	}, [persisted])

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
		}
	}, [])

	const persistedRef = useRef(persisted)
	persistedRef.current = persisted

	const saveRef = useRef(save)
	saveRef.current = save

	const persist = useCallback(async (next: string) => {
		const saved = await saveRef.current(next)
		if (saved) isDirtyRef.current = false
	}, [])

	const onChange = useCallback(
		(next: string) => {
			setValue(next)
			isDirtyRef.current = true
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
			timeoutRef.current = setTimeout(() => void persist(next), debounceMs)
		},
		[debounceMs, persist],
	)

	const onBlur = useCallback(() => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current)
		if (!isDirtyRef.current) return
		if (value.trim() === persistedRef.current.trim()) {
			isDirtyRef.current = false
			return
		}
		void persist(value)
	}, [persist, value])

	return { value, onChange, onBlur }
}
